import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { authApi } from './api';
import { FlowBackground } from './FlowBackground';
import { AuthFooter } from './AuthFooter';
import { emailSchema, type EmailInput } from '@/lib/schemas/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const t = useT();
  const form = useForm<EmailInput>({ resolver: zodResolver(emailSchema) });

  const send = useMutation({
    mutationFn: (input: EmailInput) => authApi.forgotPassword(input.email),
    onSuccess: (_data, { email }) => {
      toast({
        tone: 'success',
        title: t('auth.forgot.sentTitle'),
        description: t('auth.forgot.sentBody'),
      });
      navigate(`/auth/reset-password?email=${encodeURIComponent(email)}`);
    },
    onError: () => toast({ tone: 'error', title: t('auth.forgot.failed') }),
  });

  return (
    // `data-surface="night"` — the dark auth backdrop is theme-independent, so
    // the content tokens must be too. See styles/tokens.css.
    <div data-surface="night" className="relative grid min-h-screen lg:grid-cols-2">
      <FlowBackground />
      <div className="relative z-10 flex items-center justify-center px-6 py-12">
        <AuthFooter />
        <div
          className="w-full max-w-md rounded-2xl border border-white/[0.06] bg-[rgb(10_6_19/0.55)] p-8 shadow-[0_30px_80px_-20px_rgb(0_0_0/0.7)] backdrop-blur-xl"
          style={{
            boxShadow:
              '0 30px 80px -20px rgb(0 0 0 / 0.7), 0 0 0 1px color-mix(in srgb, var(--color-xk-accent) 8%, transparent) inset',
          }}
        >
          <div className="mb-8 flex items-center gap-2">
            <img src="/xk-icon.svg" alt="Xotski" className="h-9 w-9 rounded-lg" />
            <span className="font-display text-lg font-bold tracking-tight">xotski</span>
          </div>

          <h1 className="text-3xl font-bold">{t('auth.forgot.title')}</h1>
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
            {t('auth.forgot.subtitle')}
          </p>

          <form
            noValidate
            className="mt-8 space-y-4"
            onSubmit={form.handleSubmit((v) => send.mutate(v))}
          >
            <FormField
              label={t('auth.login.emailLabel')}
              error={form.formState.errors.email?.message}
            >
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
                <Input
                  type="email"
                  autoFocus
                  placeholder="you@brand.com"
                  className="pl-9"
                  {...form.register('email')}
                />
              </div>
            </FormField>
            <Button type="submit" loading={send.isPending} className="w-full" size="lg">
              {t('auth.forgot.submit')}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-[var(--color-fg-muted)]">
            <Link to="/login" className="font-semibold text-[var(--color-accent-ink)] hover:underline">
              {t('auth.forgot.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
